/**
 * Action Storage Interface
 *
 * Defines a pluggable storage interface for user-defined actions.
 * Implementations can use localStorage, API, or other backends.
 */

import { UserRigAction } from '../types/RiggingTypes';

/**
 * Storage interface for user-defined actions.
 * Allows swapping between localStorage and API implementations.
 */
export interface ActionStorage {
  load(videoId?: string): UserRigAction[];
  save(action: UserRigAction): void;
  update(id: string, updates: Partial<UserRigAction>): void;
  delete(id: string): void;
}

const STORAGE_KEY = 'pixle_user_actions';

/**
 * LocalStorage implementation of ActionStorage.
 * Stores user actions in browser localStorage.
 */
export class LocalActionStorage implements ActionStorage {
  private getAll(): UserRigAction[] {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      return data ? JSON.parse(data) : [];
    } catch {
      return [];
    }
  }

  private setAll(actions: UserRigAction[]): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(actions));
  }

  load(videoId?: string): UserRigAction[] {
    const all = this.getAll();
    if (videoId) {
      return all.filter((a) => a.videoId === videoId);
    }
    return all;
  }

  save(action: UserRigAction): void {
    const all = this.getAll();
    // Check for existing action with same ID
    const existingIndex = all.findIndex((a) => a.id === action.id);
    if (existingIndex >= 0) {
      all[existingIndex] = action;
    } else {
      all.push(action);
    }
    this.setAll(all);
  }

  update(id: string, updates: Partial<UserRigAction>): void {
    const all = this.getAll();
    const index = all.findIndex((a) => a.id === id);
    if (index >= 0) {
      all[index] = {
        ...all[index],
        ...updates,
        updatedAt: new Date().toISOString(),
      };
      this.setAll(all);
    }
  }

  delete(id: string): void {
    const all = this.getAll();
    const filtered = all.filter((a) => a.id !== id);
    this.setAll(filtered);
  }
}

// Default storage instance
export const defaultStorage = new LocalActionStorage();
